import { Provider as RollbarProvider, ErrorBoundary } from '@rollbar/react';
import { Modal } from 'antd';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router';

import ErrorComponent from '@/components/ErrorComponent';
import { Feedback } from '@/components/feedback/Feedback';
import Footer from '@/components/Footer';
import { Header, HeaderRefs } from '@/components/header';
import { useHeaderShadow } from '@/components/header/hooks/use-header-shadow';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AbilityProvider, AppAbility, defineAbilityFor, UserAccountList, useUser } from '@/features/auth';
import { SessionWarning } from '@/features/auth/components/SessionWarning';
import { useAuthByTemporalToken } from '@/features/auth/hooks/use-auth-by-temporal-token';
import ContactInformationModalContainer from '@/features/contracts/components/ContactInformationModalContainer';
import useClarity from '@/hooks/use-clarity';
import { useLocale } from '@/hooks/use-locale';
import { useOutsideClick } from '@/hooks/use-outside-click';
import { useHeaderStore } from '@/store';
import { useUserInfoStore } from '@/store/use-user-info-store';
import AlertsContainer from '@/widgets/Alerts/AlertsContainer';
import RollbarWrapper from '@/wrappers/RollbarWrapper';

import { rollbarConfig } from '../../rollbar.config';

const AppLayout = () => {
  const { data: user, isLoading } = useUser();
  const { t } = useTranslation();
  const mainRef = useRef<HTMLElement>(null);

  useAuthByTemporalToken();
  useClarity();

  const { selectedAccount, hasSelectedAccountFirstTime, setHasSelectedAccountFirstTime } = useUserInfoStore();
  const { setIsLocaleDropdownVisible, setIsMobileMenuVisible, setIsSearchInputVisible, setIsUserDropdownVisible } = useHeaderStore();
  const headerRef = useRef<HeaderRefs>();

  const hasHeaderShadow = useHeaderShadow(mainRef);

  useLocale();

  useEffect(() => {
    if (CSS.supports('height', '1dvh')) {
      document.documentElement.style.setProperty('--viewport-height-unit', '1dvh');
    } else {
      document.documentElement.style.setProperty('--viewport-height-unit', '1vh');
    }
  });

  useOutsideClick(
    [
      headerRef?.current?.localeElementRef?.current?.buttonRef,
      headerRef?.current?.localeElementRef?.current?.iconDownRef,
      headerRef?.current?.localeElementRef?.current?.iconUpRef,
      headerRef?.current?.localeElementRef?.current?.parentDivRef,
      headerRef?.current?.userElementRef,
      headerRef?.current?.mobileMenuElementRef,
      headerRef?.current?.searchElementRef,
    ],
    () => {
      setIsLocaleDropdownVisible(false);
      setIsUserDropdownVisible(false);
      setIsMobileMenuVisible(false);
      setIsSearchInputVisible(false);
    },
  );

  const ability: AppAbility | null = user ? defineAbilityFor(user as any, selectedAccount?.code) : null;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <RollbarProvider config={rollbarConfig}>
      <ErrorBoundary fallbackUI={ErrorComponent}>
        <RollbarWrapper>
          <AbilityProvider ability={ability}>
            <div className="flex flex-col justify-between bg-gray-100 h-screen">
              <Header hasHeaderShadow={hasHeaderShadow} ref={headerRef} />
              <AlertsContainer />
              <main ref={mainRef} className="flex-1 pt-5 flex flex-col w-full overflow-y-auto">
                <div className="flex-1">
                  <Outlet />
                </div>
                <Footer className="h-15" />
              </main>
            </div>
            {user && (
              <>
                <Modal
                  title={`${t('hello', { ns: 'common' })}, ${user?.fullName}`}
                  open={!hasSelectedAccountFirstTime}
                  footer={null}
                  onCancel={() => {
                    setHasSelectedAccountFirstTime(true);
                  }}
                >
                  <div className="flex flex-col gap-5 max-h-[70vh] overflow-y-auto">
                    <span className="text-gray-400 text-small">{t('roleChoose', { ns: 'common' })}</span>
                    <UserAccountList
                      className="mt-5"
                      accounts={user?.accounts}
                      onAccountSelect={() => {
                        setHasSelectedAccountFirstTime(true);
                      }}
                    />
                  </div>
                </Modal>
                <SessionWarning />
              </>
            )}
            <ContactInformationModalContainer />
            <Feedback feedbackUrl={import.meta.env.VITE_FEEDBACK_URL ?? '#'} />
          </AbilityProvider>
        </RollbarWrapper>
      </ErrorBoundary>
    </RollbarProvider>
  );
};

export default AppLayout;
